import { useTranslation } from '@chr/online-shared-ui-localizations';
import { Box, Icon, Stack, Tooltip, Typography } from '@mui/material';

import { Location } from '@/common/apis/dtos';
import { RiskLevelType } from '@/common/constants/booking-types';
import { RiskLevel } from '@/common/components/risk-level/RiskLevel';
import { formatLocation, IsoToDateAtTime } from '@/common/utilities/display-utilities';

import EtaDataSource from './EtaDataSource';

export interface EtaTrackingProps {
  displayEtaDateTime?: string | undefined;
  displayEtaSource: string | undefined;
  location?: Location;
  riskLevel: string;
}

function EtaLocation({ location }: { location: Location }) {
  const translate = useTranslation();

  const locationName = location?.name || '';
  const locationAddress = formatLocation(location);
  const screenReaderText = `${translate('Estimated Arrival:')} ${translate('{0} in {1}', [
    locationName,
    locationAddress
  ])}`;

  const tooltipContent = (
    <>
      {locationName && (
        <Box data-testid="locationName">
          {translate('To')} {locationName}
        </Box>
      )}
      {locationAddress && <Box data-testid="locationAddress">{locationAddress}</Box>}
    </>
  );

  return (
    <Tooltip title={tooltipContent} placement="top">
      <Icon
        color="primary"
        aria-hidden="false"
        aria-label={screenReaderText}
        sx={{ marginRight: 0.5 }}
      >
        info
      </Icon>
    </Tooltip>
  );
}

const EtaTracking = ({ displayEtaSource, displayEtaDateTime, location, riskLevel }: EtaTrackingProps) => {
  const translate = useTranslation();
  const showRiskLevel = riskLevel !== RiskLevelType.Unassigned;

  return (
    <Stack
      direction={'row'}
      alignItems={{ xs: 'center', sm: 'normal' }}
      data-testid="eta-container"
    >
      {displayEtaDateTime &&
        <>
          <Stack direction="row" data-testid="eta-source">
            {displayEtaSource && <EtaDataSource displayEtaSource={displayEtaSource} />}
            {location && <EtaLocation location={location} />}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} sx={{ marginRight: '12px' }}>
            <Typography variant="subtitle1" color="text.primary">
              {translate('Estimated Arrival:')}
            </Typography>

            <Typography variant="subtitle1" color="text.primary" fontWeight={600}
                        data-testid="estimated-arrival-field-value">
              {IsoToDateAtTime(displayEtaDateTime, translate)}
            </Typography>
          </Stack>
        </>
      }

      {showRiskLevel &&
        <Box data-testid="eta-risklevel">
          <RiskLevel riskLevel={riskLevel} />
        </Box>
      }
    </Stack>
  );
};

export default EtaTracking;
